import BackButton from "@src/common/components/back-button";
import AppContainer from "@src/common/app_container";
import React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {SubmitHandler, useFieldArray, useForm} from "react-hook-form";
import Alert, {AlertProps} from "@src/common/alert";
import {BusinessProfile, DeepResearchWorkflowExecution, Document} from "@src/model/business_profile";
// import { SocialAccount } from "@src/model/licensable_property";
import {useBusinessProfileAPI} from "@src/api/business_profile_api";
import {extractSocialMediaInfo} from "@src/lib/utils";

export default function BusinessProfileEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const businessProfileAPI = useBusinessProfileAPI();
  const [deepResearchModel, setDeepResearchModel] = React.useState<"GEMINI" | "OPENAI">("GEMINI");
  const [fileToUpload, setFileToUpload] = React.useState<File | null>(null);
  const [usageForUpload, setUsageForUpload] = React.useState<string>("BUSINESS_INTELLIGENCE");
  const [isViewModalOpen, setIsViewModalOpen] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<Document | null>(null);
  const [documentContent, setDocumentContent] = React.useState<string>("");
  const [isFixingJson, setIsFixingJson] = React.useState<string | null>(null);
    const [researchTasks, setResearchTasks] = React.useState<DeepResearchWorkflowExecution[]>([]);
    const [isResearchLoading, setIsResearchLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<BusinessProfile>();

  // Documents
  const { fields: documents, remove: removeDocument } = useFieldArray({
    control,
    name: "documents",
    keyName: "fieldId" // Use a different key name to avoid conflicts with document.id
  });


  React.useEffect(() => {
    if (id) {
      businessProfileAPI.getBusinessProfile(id)
        .then((profile) => {
          if (profile.documents) {
            profile.documents = profile.documents.map(doc => ({
              ...doc,
              id: doc.id ? String(doc.id) : undefined
            }));
          }

          reset(profile);
        })
        .catch((err) => {
          setStatus({ type: "error", message: "Error loading profile: " + err.message });
        });
    }
  }, [id]);

  const onSubmit: SubmitHandler<BusinessProfile> = (data) => {
    setIsSaving(true);
    if (typeof (data.social_accounts) === "string") {
      if ((data.social_accounts as string) && (data.social_accounts as string).length > 0) {
        let parts = (data.social_accounts as string).split(',').map(part => part.trim());
        data.social_accounts = []
        for (let socialLink of parts) {
          var socialInfo = extractSocialMediaInfo(socialLink);
          if (socialInfo) {
            data.social_accounts.push(
              {
                provider: socialInfo.platform,
                link: socialLink,
                external_id: socialInfo.username,
              }
            )
          }
        }
      } else {
        data.social_accounts = []
      }
    }
    const savePromise = id
      ? businessProfileAPI.updateBusinessProfile(id, data)
      : businessProfileAPI.createBusinessProfile(data);

    savePromise
      .then(() => {
        setIsSaving(false);
        setStatus({ type: "success", message: "Business profile saved successfully" });
        setTimeout(() => {
          navigate("/business-profiles");
        }, 2000);
      })
      .catch((err) => {
        setIsSaving(false);
        setStatus({ type: "error", message: "Error saving profile: " + err.message });
      });
  };

  const handleUploadDocument = () => {
    if (!id) {
      setStatus({ type: "error", message: "Please save the profile before adding documents." });
      return;
    }
    if (fileToUpload) {
      setIsSaving(true);
      businessProfileAPI.addDocument(id, fileToUpload, usageForUpload)
        .then(() => {
          setFileToUpload(null);
          // To see the new document, we need to refresh the profile data.
          businessProfileAPI.getBusinessProfile(id)
            .then((profile) => {
              if (profile.documents) {
                profile.documents = profile.documents.map(doc => ({
                  ...doc,
                  id: doc.id ? String(doc.id) : undefined
                }));
              }
              reset(profile);
              setStatus({ type: "success", message: "Document uploaded successfully." });
            })
            .catch((err) => {
              setStatus({ type: "error", message: "Error reloading profile: " + err.message });
            });
        })
        .catch((err) => {
          setStatus({ type: "error", message: `Error uploading document: ${err.message}` });
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setIsViewModalOpen(true);
    setDocumentContent(document.content || "");
  };


  const handleRemoveDocument = async (index: number) => {
    // If profile not saved yet or the document has no id, just remove locally
    const uiDoc: any = documents[index];

    if (!id || !uiDoc?.id) {
      removeDocument(index);
      return;
    }

    try {
      setIsSaving(true);

      const documentId = String(uiDoc.id);
      await businessProfileAPI.removeDocument(id, documentId);
      const refreshed = await businessProfileAPI.getBusinessProfile(id);

      if (refreshed.documents) {
        refreshed.documents = refreshed.documents.map(doc => ({
          ...doc,
          id: doc.id ? String(doc.id) : undefined
        }));
      }
      reset(refreshed);
      setStatus({ type: "success", message: "Document removed." });
    } catch (err: any) {
      setStatus({ type: "error", message: `Error removing document: ${err.message || err}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunBusinessIntelligence = async () => {
    if (!id) return;
    try {
      setIsSaving(true);
      await businessProfileAPI.runBusinessIntelligenceDeepResearch(id, deepResearchModel);
      setStatus({ type: "success", message: `Deep research started (${deepResearchModel}). Check Research Workflows below for progress.` });
        // Refresh research status after starting
        loadResearchStatus();
    } catch (err: any) {
      setStatus({ type: "error", message: `Error starting deep research: ${err?.message || err}` });
    } finally {
      setIsSaving(false);
    }
  };

    const loadResearchStatus = async () => {
        if (!id) return;
        try {
            setIsResearchLoading(true);
            const tasks = await businessProfileAPI.getResearchStatus(id);
            setResearchTasks(tasks);
        } catch (err: any) {
            console.error("Error loading research status:", err);
        } finally {
            setIsResearchLoading(false);
        }
    };

    // Load research status on mount if profile exists
    React.useEffect(() => {
        if (id) {
            loadResearchStatus();
        }
    }, [id]);

    const handleFixDocumentJson = async (doc: any) => {
        const documentId = String(doc.id);
        if (!documentId || !id) return;
        if (!window.confirm("This will attempt to repair the document's JSON structure using AI. The original content will be overwritten. Continue?")) return;
        try {
            setIsFixingJson(documentId);
            await businessProfileAPI.fixDocumentJson(documentId);
            // Refresh profile data
            const profile = await businessProfileAPI.getBusinessProfile(id);
            if (profile.documents) {
                profile.documents = profile.documents.map(d => ({
                    ...d,
                    id: d.id ? String(d.id) : undefined
                }));
            }
            reset(profile);
            setStatus({ type: "success", message: "Document JSON repaired successfully." });
        } catch (err: any) {
            setStatus({ type: "error", message: `Error fixing document JSON: ${err?.message || err}` });
        } finally {
            setIsFixingJson(null);
        }
    };

    const getTaskStatusBadge = (status: string) => {
        switch (status) {
            case "completed":
                return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">Completed</span>;
            case "failed":
                return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm">Failed</span>;
            case "processing":
                return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm">Processing</span>;
            case "queued":
                return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">Queued</span>;
            default:
                return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">{status}</span>;
        }
    };


  return (
    <AppContainer>
      <div className="container pb-40">
        {isViewModalOpen && selectedDocument && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">

              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{selectedDocument.name}</h2>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedDocument(null);
                    setDocumentContent("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <span className="mr-4">Usage: {selectedDocument.usage}</span>
                <span className="mr-4">Version: {selectedDocument.version}</span>
                {selectedDocument.updated_at && (
                  <span>Last Updated: {new Date(selectedDocument.updated_at).toLocaleString()}</span>
                )}
              </div>

              <div className="p-6 overflow-auto flex-1">

                <div className="prose max-w-none whitespace-pre-wrap">
                  {documentContent || "No content available"}
                </div>

              </div>
            </div>
          </div>
        )}


        <BackButton variant="ghost" onClick={() => navigate("/business-profiles")} />
        <div className="flex justify-between">
          <h1 className="text-3xl">{id ? "Edit Business Profile" : "Add Business Profile"}</h1>

          <div className="flex space-x-2">
            <button
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              type="submit"
              form="business_profile_form"
              disabled={isSaving}
            >
              Save
            </button>


          </div>
        </div>

        {id && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="font-semibold">Deep Research:</span>
            <select
              className="border rounded px-2 py-1 w-[250px]"
              value={deepResearchModel}
              onChange={(e) => setDeepResearchModel(e.target.value as "GEMINI" | "OPENAI")}
              disabled={isSaving}
            >
              <option value="GEMINI">Gemini</option>
              <option value="OPENAI">OpenAI</option>
            </select>
            <button
              type="button"
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400"
              onClick={handleRunBusinessIntelligence}
              disabled={isSaving}
            >
              Run Business Intelligence
            </button>
          </div>
        )}

          {/* Research Workflow Executions */}
          {id && researchTasks.length > 0 && (
              <div className="mt-4 border rounded p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-lg">Research Workflows</h3>
                      <button
                          type="button"
                          className="bg-gray-500 hover:bg-gray-600 text-white text-sm py-1 px-3 rounded disabled:bg-gray-300"
                          onClick={loadResearchStatus}
                          disabled={isResearchLoading}
                      >
                          {isResearchLoading ? "Loading..." : "Refresh"}
                      </button>
                  </div>
                  <div className="space-y-2">
                      {researchTasks.map((execution) => {
                          const provider = execution.input_data?.provider || "Unknown";
                          const researchType = execution.input_data?.research_type || "Unknown";
                          const createdAt = execution.created_at ? new Date(execution.created_at).toLocaleString() : "Unknown";
                          const status = execution.status?.toUpperCase() || "UNKNOWN";

                          return (
                              <div key={execution.id} className="border rounded p-3 bg-white">
                                  <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                              <span className="font-medium">{provider}</span>
                                              <span className="text-gray-500 text-sm">({researchType})</span>
                                              {getTaskStatusBadge(status.toLowerCase())}
                                          </div>
                                          <div className="text-sm text-gray-600">
                                              <div>Workflow ID: <code className="bg-gray-100 px-1 rounded text-xs">{execution.id}</code></div>
                                              <div>Created: {createdAt}</div>
                                              {execution.error_message && (
                                                  <div className="text-red-600 mt-1">Error: {execution.error_message}</div>
                                              )}
                                          </div>
                                          {execution.steps && execution.steps.length > 0 && (
                                              <div className="mt-2 text-xs text-gray-500">
                                                  {execution.steps.map((step, idx) => (
                                                      <span key={step.id} className="mr-3">
                                                          {step.name || step.workflow_step?.step_name || `Step ${idx + 1}`}: {getTaskStatusBadge(step.status?.toLowerCase() || "unknown")}
                                                      </span>
                                                  ))}
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          );
                      })}
                  </div>
              </div>
          )}

        {status && <Alert {...status} />}
        <form onSubmit={handleSubmit(onSubmit)} id="business_profile_form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Name</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && <span className="text-red-500">{errors.name.message}</span>}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Organization ID</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("organization_id")}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Industry</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("industry")}
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Address</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("address_line_1")}
                />
              </div>
            </div>


            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("address_line_2")}
                />
              </div>
            </div>
          </div>
          <div className="mt-2 flex space-x-2">
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">City</label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    {...register("city")}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">State</label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    {...register("state")}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">Zip</label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    {...register("zip")}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">Country</label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                  <input
                    className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    {...register("country")}
                  />
                </div>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Website URL</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("website_url")}
                />
              </div>
            </div>

          </div>
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">Social Accounts</label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 sm:max-w-md">
                <input
                  className="block flex-1 border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  {...register("social_accounts")}
                />
              </div>
            </div>
          </div>

          {/* Campaigns */}
          <div>
            <label className="block font-bold">Campaigns</label>
            <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-2" onClick={() => navigate(`/business-profiles/${id}/campaigns`)}>Manage Campaigns</button>
          </div>

          {/* Documents */}
          <div className="w-full space-y-4">
            <label className="block font-bold">Documents</label>

            {id && (
              <div className="border p-4 mt-4">
                <h3 className="font-semibold mb-2">Add New Document</h3>
                <div className="flex w-full space-x-4">
                  <input
                    type="file"
                    className="border rounded p-1"
                    onChange={(e) => setFileToUpload(e.target.files ? e.target.files[0] : null)}
                  />

                  <select
                    className="border rounded px-2 py-1 w-[250px]"
                    value={usageForUpload}
                    onChange={(e) => setUsageForUpload(e.target.value)}
                  >
                    <option value="BUSINESS_INTELLIGENCE">Business Intelligence</option>
                    <option value="STRATEGY">Strategy</option>
                    <option value="PERSONA">Persona</option>
                  </select>
                  <button
                    type="button"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400"
                    onClick={handleUploadDocument}
                    disabled={!fileToUpload || isSaving}
                  >
                    {isSaving ? "Uploading..." : "Upload Document"}
                  </button>
                </div>
              </div>
              // </div>
            )}
            {documents.map((doc, dIdx) => (
              <div key={doc.fieldId} className="border p-2 mb-2 flex items-center justify-between">
                <div>
                  <span className="font-medium pr-4">{doc.name}</span>
                  <span className="text-gray-500">({doc.usage})</span>
                </div>
                <div className="flex gap-6">
                  {(doc.usage === "BUSINESS_INTELLIGENCE" || doc.usage === "PERSONA") && (
                    <button
                      type="button"
                      onClick={() => handleFixDocumentJson(doc)}
                      className="text-orange-500 hover:text-orange-700 disabled:text-gray-400"
                      disabled={isFixingJson === String(doc.id)}
                    >
                      {isFixingJson === String(doc.id) ? "Fixing..." : "Fix JSON"}
                    </button>
                  )}
                  <button type="button" onClick={() => handleViewDocument(doc as Document)} className="text-blue-500">View</button>
                  <button type="button" onClick={() => handleRemoveDocument(dIdx)} className="text-red-500">Remove</button>
                </div>
              </div>
            ))}



          </div>
        </form>
      </div>
    </AppContainer >
  )
}
